import {
  AnchorType,
  CheckStatus,
  EntityRefUtil,
  formatDateWithUserTimezone,
  GetChecksRequestDTO,
  GetChecksResponseDTO,
  GetUnitsResponseDTO,
  isCheckCompleted,
  RecordState,
  TagEntity,
} from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { TooltipArrow } from '@radix-ui/react-tooltip';
import { AccessorKeyColumnDef, createColumnHelper } from '@tanstack/react-table';
import { match } from 'ts-pattern';

import CheckIcon from '@/components/icons/check';
import InformationIcon from '@/components/icons/information';
import { ASSET_SECTION_LABEL, createAssetTagsColumn } from '@/components/table-page/columns/asset-tags-column';
import { createTagsColumn } from '@/components/table-page/columns/tags-column';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { UserAvatarLabel } from '@/components/users/user-avatar-label';
import { initials } from '@/lib/utils';
import { ApiService } from '@/services/api-v2';
import { useUserStore } from '@/stores/user.store';
import { getAvatarBackgroundColorClass } from '@/utils/avatar';
import { formatScheduleCheckLabel } from '@/utils/schedules';

const columnHelper = createColumnHelper<GetChecksResponseDTO['data'][number]>();

export const createCheckTableColumns = (opts?: { units?: GetUnitsResponseDTO['data']; onWorkOrderClick?: (workOrderId: string) => void }) => {
  const userTimezone = useUserStore.getState().user?.timezone;
  return [
    // 1. Select checkbox
    columnHelper.display({
      id: 'select',
      header: ({ table }) => (
        <div className="flex w-full justify-center">
          <label className="-m-2 p-2">
            <Checkbox
              checked={table.getIsAllPageRowsSelected()}
              onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
              aria-label="Select all"
            />
          </label>
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex w-full justify-center">
          <label className="-m-2 p-2" onClick={(e) => e.stopPropagation()}>
            <Checkbox checked={row.getIsSelected()} onCheckedChange={(value) => row.toggleSelected(!!value)} aria-label="Select row" />
          </label>
        </div>
      ),
      meta: {
        cellClassName: 'w-[40px] min-w-[40px] max-w-[40px]',
      },
      enableSorting: false,
      enableHiding: false,
    }),
    // 2. Asset
    columnHelper.accessor('asset', {
      header: 'Asset',
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? <span>{EntityRefUtil.createAssetLabel(value)}</span> : <span className="text-secondary-text">-</span>;
      },
      enableGrouping: true,
      enableColumnFilter: true,
      getGroupingValue: (row) => (row.asset?.internalId ? EntityRefUtil.createAssetLabel(row.asset) : 'None'),
      meta: {
        columnGroup: ASSET_SECTION_LABEL,
        filterType: 'async-multi-select',
        getFilterOptionsFn: async (inputValue, value) => {
          const filters = inputValue ? { filters: { search: inputValue } } : undefined;
          const { data } = await (await ApiService.GetAssets({ limit: 100, filters: { ...filters, state: RecordState.Active } })).json();

          const resultsMap = new Map(data.map((asset) => [asset.id, { label: EntityRefUtil.createAssetLabel(asset), value: asset.id }]));

          if (value?.length) {
            const missingIds = Array.isArray(value) ? value.filter((id) => !resultsMap.has(id)) : [];
            if (missingIds.length) {
              const { data: missingAssets } = await (
                await ApiService.GetAssets({
                  limit: 100,
                  filters: { fields: { id: missingIds }, state: RecordState.Active },
                })
              ).json();

              for (const asset of missingAssets) {
                resultsMap.set(asset.id, { label: EntityRefUtil.createAssetLabel(asset), value: asset.id });
              }
            }
          }

          return Array.from(resultsMap.values());
        },
      },
    }),
    // 3. Asset Category
    columnHelper.accessor((row) => row.asset?.category?.name, {
      id: 'assetCategory',
      header: 'Asset Category',
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? <span>{value}</span> : <span className="text-secondary-text">-</span>;
      },
      enableGrouping: true,
      enableColumnFilter: true,
      getGroupingValue: (row) => row.asset?.category?.name || 'None',
      meta: {
        columnGroup: ASSET_SECTION_LABEL,
        filterType: 'async-multi-select',
        getFilterOptionsFn: async (inputValue, value) => {
          const filters = inputValue ? { search: inputValue } : undefined;
          const { data } = await (await ApiService.GetAssetCategories({ limit: 100, filters: { ...filters } })).json();

          const resultsMap = new Map(data.map((category) => [category.id, { label: category.name, value: category.id }]));

          if (value?.length) {
            const missingIds = Array.isArray(value) ? value.filter((id) => !resultsMap.has(id)) : [];
            if (missingIds.length) {
              const { data: missingCategories } = await (
                await ApiService.GetAssetCategories({
                  limit: 100,
                  filters: { fields: { id: missingIds } },
                })
              ).json();

              for (const category of missingCategories) {
                resultsMap.set(category.id, { label: category.name, value: category.id });
              }
            }
          }

          return Array.from(resultsMap.values());
        },
      },
    }),
    // 3b. Asset Location
    columnHelper.accessor((row) => row.asset?.location?.name, {
      id: 'assetLocation',
      header: 'Asset Location',
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? <span>{value}</span> : <span className="text-secondary-text">-</span>;
      },
      enableGrouping: true,
      enableColumnFilter: true,
      getGroupingValue: (row) => row.asset?.location?.name || 'None',
      meta: {
        columnGroup: ASSET_SECTION_LABEL,
        filterType: 'async-multi-select',
        getFilterOptionsFn: async (inputValue, value) => {
          const filters = inputValue ? { search: inputValue } : undefined;
          const { data } = await (await ApiService.GetLocations({ limit: 100, filters: { ...filters, state: RecordState.Active } })).json();

          const resultsMap = new Map(data.map((location) => [location.id, { label: location.name, value: location.id }]));

          if (value?.length) {
            const missingIds = Array.isArray(value) ? value.filter((id) => !resultsMap.has(id)) : [];
            if (missingIds.length) {
              const { data: missingLocations } = await (
                await ApiService.GetLocations({
                  limit: 100,
                  filters: { fields: { id: missingIds }, state: RecordState.Active },
                })
              ).json();

              for (const location of missingLocations) {
                resultsMap.set(location.id, { label: location.name, value: location.id });
              }
            }
          }

          return Array.from(resultsMap.values());
        },
      },
    }),
    // 3c. Asset Tags
    createAssetTagsColumn(columnHelper),
    // 4. Status
    columnHelper.accessor('status', {
      header: 'Status',
      cell: ({ getValue }) => {
        const status = getValue();

        const badgeClasses = match(status)
          .with(CheckStatus.Open, () => 'bg-grey-1 text-default-text')
          .with(CheckStatus.Closed, () => 'bg-default-light-green text-default-dark-green')
          .with(CheckStatus.Void, () => 'bg-default-light-violet text-default-dark-violet')
          .exhaustive();

        const statusText = match(status)
          .with(CheckStatus.Open, () => 'Open')
          .with(CheckStatus.Closed, () => 'Closed')
          .with(CheckStatus.Void, () => 'Void')
          .exhaustive();

        return (
          <Badge size="sm" className={badgeClasses}>
            {statusText}
          </Badge>
        );
      },
      enableGrouping: true,
      enableColumnFilter: true,
      getGroupingValue: (row) => row.status || 'None',
      meta: {
        filterType: 'multi-select',
        disableNullFilter: true,
        getFilterOptionsFn: async () => {
          return Object.values(CheckStatus).map((status) => ({ label: status.charAt(0).toUpperCase() + status.slice(1), value: status }));
        },
      },
    }),
    // 5. Schedule Check (with trigger summary, e.g. "B - 500 Kms or 6 m")
    columnHelper.accessor((row) => formatScheduleCheckLabel(row), {
      id: 'scheduleCheck',
      header: 'Schedule Check',
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? <span>{value}</span> : <span className="text-secondary-text">-</span>;
      },
      enableGrouping: false,
      enableColumnFilter: false,
      enableSorting: false,
    }),
    // 6. Due Progress
    columnHelper.accessor('dueProgress', {
      header: 'Due Progress',
      cell: ({ getValue, row }) => {
        const value = getValue();
        const isUnlinked = row.original.isUnlinked;
        const allUnitProgress = row.original.allUnitProgress;
        const anchorType = row.original.schedule?.triggers?.[0]?.anchorType;

        // Show check mark once the check has been completed — closed, or left open by
        // a submission the workspace's auto-close setting keeps open for manual review
        if (isCheckCompleted(row.original)) {
          return (
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <CheckIcon className="text-default-green h-5 w-5" />
                </TooltipTrigger>
                <TooltipContent className="bg-default-text pointer-events-none border-none py-1 text-xs text-white">
                  <TooltipArrow className="fill-default-text" />
                  Schedule Check is complete
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        }

        if (isUnlinked) {
          const hasScheduleCycle = row.original.scheduleCycleId != null;
          const unlinkedTooltip =
            anchorType === AnchorType.Crawling && hasScheduleCycle
              ? 'Due progress appears after the first check is closed'
              : "Pick a schedule check to define this asset's starting position";
          return (
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <div className="flex w-full items-center">
                    <div className={`h-3 flex-1 rounded-l-full ${row.getIsSelected() ? 'bg-grey-3' : 'bg-grey-2'}`} />
                    <InformationIcon className="text-grey-5 mx-1 h-4 w-4 shrink-0" />
                    <div className={`h-3 flex-1 rounded-r-full ${row.getIsSelected() ? 'bg-grey-3' : 'bg-grey-2'}`} />
                  </div>
                </TooltipTrigger>
                <TooltipContent className="bg-default-text pointer-events-none border-none py-1 text-xs text-white">
                  <TooltipArrow className="fill-default-text" />
                  {unlinkedTooltip}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        }

        if (value == null) return <span className="text-secondary-text">-</span>;

        const dateRemaining = row.original.dateRemaining;
        const allDateProgress = row.original.allDateProgress;
        const status = (() => {
          if (value >= 100 || (dateRemaining && dateRemaining.days <= 0)) return 'overdue';
          if (value <= 75) return 'ok';
          return 'due';
        })();

        const colors = match(status)
          .with('overdue', () => ({
            primary: 'rgb(var(--default-red))',
            secondary: 'rgb(var(--secondary-red))',
          }))
          .with('due', () => ({
            primary: 'rgb(var(--default-yellow))',
            secondary: 'rgb(var(--secondary-yellow))',
          }))
          .with('ok', () => ({
            primary: 'rgb(var(--default-green))',
            secondary: 'rgb(var(--secondary-green))',
          }))
          .otherwise(() => ({
            primary: 'rgb(var(--default-green))',
            secondary: 'rgb(var(--secondary-green))',
          }));

        const progressBar = (
          <div className="bg-grey-2 h-3 w-full overflow-hidden rounded-full">
            <div
              className="h-full rounded-full"
              style={{
                width: value < 0 ? 0 : `max(12px, ${Math.min(value, 100)}%)`,
                background: `repeating-linear-gradient(
                45deg,
                ${colors.primary},
                ${colors.primary} 8px,
                ${colors.secondary} 8px,
                ${colors.secondary} 16px
              )`,
              }}
            />
          </div>
        );

        const hasDateContent = allDateProgress && allDateProgress.length > 0;
        const hasUnitContent = allUnitProgress && allUnitProgress.length > 0;
        const hasTooltipContent = hasUnitContent || hasDateContent || dateRemaining;

        if (hasTooltipContent) {
          return (
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>{progressBar}</TooltipTrigger>
                <TooltipContent className="bg-default-text pointer-events-none border-none py-1 text-xs text-white">
                  <TooltipArrow className="fill-default-text" />
                  <ul className="flex flex-col gap-1">
                    {allUnitProgress?.map((unit, index) => (
                      <li key={`unit-${index}`} className="flex items-center gap-2 text-xs">
                        <span>•</span>
                        <span>
                          {unit.remaining.toLocaleString()} {unit.unitAbbreviation} remaining{' '}
                          <span className="font-semibold">({unit.progress.toFixed(0)}%)</span>
                        </span>
                      </li>
                    ))}
                    {hasDateContent
                      ? allDateProgress.map((d, index) => {
                          const unitLabel = match(d.calendarUnit)
                            .with('d', () => 'Days')
                            .with('w', () => 'Weeks')
                            .with('m', () => 'Months')
                            .exhaustive();
                          return (
                            <li key={`date-${index}`} className="flex items-center gap-2 text-xs">
                              <span>•</span>
                              <span>
                                {`${unitLabel}: ${d.days} days remaining`} <span className="font-semibold">({d.progress.toFixed(0)}%)</span>
                              </span>
                            </li>
                          );
                        })
                      : dateRemaining && (
                          <li className="flex items-center gap-2 text-xs">
                            <span>•</span>
                            <span>{`${dateRemaining.days} days remaining`}</span>
                          </li>
                        )}
                  </ul>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        }

        return progressBar;
      },
      meta: {
        cellClassName: 'min-w-[180px]',
      },
      enableSorting: true,
      enableGrouping: false,
      enableColumnFilter: false,
    }),
    // 7. Dynamic unit remaining columns — one per workspace unit
    ...(opts?.units
      ? opts.units.map((unit) =>
          columnHelper.accessor((row) => row.allUnitProgress?.find((u) => u.unitName === unit.name)?.remaining, {
            id: `unitRemaining-${unit.id}`,
            header: `${unit.abbreviation} Remaining`,
            cell: ({ row }) => {
              if (isCheckCompleted(row.original)) {
                return <span className="text-secondary-text">-</span>;
              }
              const unitProgress = row.original.allUnitProgress?.find((u) => u.unitName === unit.name);
              if (!unitProgress) return <span className="text-secondary-text">-</span>;
              return (
                <span>
                  {unitProgress.remaining.toLocaleString()} {unitProgress.unitAbbreviation}
                </span>
              );
            },
            enableSorting: false,
            enableGrouping: false,
            enableColumnFilter: false,
          }),
        )
      : []),
    // 8. Date Remaining (shows all date-based triggers, not just the winning one)
    columnHelper.accessor('dateRemaining', {
      id: 'dateRemaining',
      header: 'Date Remaining',
      cell: ({ row }) => {
        if (isCheckCompleted(row.original)) {
          return <span className="text-secondary-text">-</span>;
        }
        const allDateProgress = row.original.allDateProgress;
        if (allDateProgress && allDateProgress.length > 0) {
          return <span>{allDateProgress.map((d) => `${d.days} days`).join(', ')}</span>;
        }
        const dateRemaining = row.original.dateRemaining;
        if (!dateRemaining) return <span className="text-secondary-text">-</span>;
        return <span>{`${dateRemaining.days} days`}</span>;
      },
      enableSorting: false,
      enableGrouping: false,
      enableColumnFilter: false,
    }),
    // 9. Schedule
    columnHelper.accessor((row) => row.schedule?.name, {
      id: 'schedule',
      header: 'Schedule',
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? <span>{value}</span> : <span className="text-secondary-text">-</span>;
      },
      enableGrouping: true,
      enableColumnFilter: true,
      getGroupingValue: (row) => row.schedule?.name || 'None',
      meta: {
        filterType: 'async-multi-select',
        getFilterOptionsFn: async (inputValue, value) => {
          const filters = inputValue ? { search: inputValue } : undefined;
          const { data } = await (await ApiService.GetSchedules({ limit: 100, filters: { ...filters, state: RecordState.Active } })).json();

          const resultsMap = new Map(data.map((schedule) => [schedule.id, { label: schedule.name, value: schedule.id }]));

          if (value?.length) {
            const missingIds = Array.isArray(value) ? value.filter((id) => !resultsMap.has(id)) : [];
            if (missingIds.length) {
              const { data: missingSchedules } = await (
                await ApiService.GetSchedules({
                  limit: 100,
                  filters: { fields: { id: missingIds }, state: RecordState.Active },
                })
              ).json();

              for (const schedule of missingSchedules) {
                resultsMap.set(schedule.id, { label: schedule.name, value: schedule.id });
              }
            }
          }

          return Array.from(resultsMap.values());
        },
      },
    }),
    // 10. Created Date
    columnHelper.accessor((row) => formatDateWithUserTimezone(new Date(row.createdAt), userTimezone), {
      id: 'createdDate',
      header: 'Created Date',
      enableSorting: true,
      enableGrouping: false,
      meta: {
        filterType: 'date-range',
      },
    }),
    // 11. Completed Date
    columnHelper.accessor((row) => (row.completedAt ? formatDateWithUserTimezone(new Date(row.completedAt), userTimezone) : null), {
      id: 'completedDate',
      header: 'Completed Date',
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? <span>{value}</span> : <span className="text-secondary-text">-</span>;
      },
      enableSorting: true,
      enableGrouping: false,
      meta: {
        filterType: 'date-range',
      },
    }),
    // 12. Due Date
    columnHelper.accessor((row) => (row.dueDate ? formatDateWithUserTimezone(new Date(row.dueDate), userTimezone) : null), {
      id: 'dueDate',
      header: 'Due Date',
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? <span>{value}</span> : <span className="text-secondary-text">-</span>;
      },
      enableSorting: false,
      enableGrouping: false,
      meta: {
        filterType: 'date-range',
        filterLabel: 'Due Date',
      },
    }),
    // 13. Due Reading
    columnHelper.accessor((row) => (row.dueReading != null ? `${row.dueReading.toLocaleString()} ${row.unitRemaining?.unitAbbreviation ?? ''}`.trim() : null), {
      id: 'dueReading',
      header: 'Due Reading',
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? <span>{value}</span> : <span className="text-secondary-text">-</span>;
      },
      enableSorting: false,
      enableGrouping: false,
      enableColumnFilter: false,
    }),
    // 14. Completed By
    columnHelper.accessor((row) => row.completedByUser, {
      id: 'completedBy',
      header: 'Completed By',
      cell: ({ row }) => {
        const check = row.original;
        const completedBy = check.completedByUser;
        const fullName = EntityRefUtil.createUserFullNameRef(completedBy);
        const value = fullName || '-';
        const avatarBackgroundColorClass = getAvatarBackgroundColorClass(completedBy?.firstName || '');
        const completedByFullInitials = initials(fullName);
        return (
          <UserAvatarLabel
            value={value}
            operatedByFullInitials={completedByFullInitials}
            avatarUrl=""
            avatarBackgroundColorClass={avatarBackgroundColorClass}
          />
        );
      },
      getGroupingValue: (row) => {
        return EntityRefUtil.createUserFullNameRef(row.completedByUser, 'None');
      },
      enableGrouping: true,
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        filterType: 'async-multi-select',
        getFilterOptionsFn: async (inputValue, value) => {
          const filters = inputValue ? { filters: { search: inputValue } } : undefined;
          const { data: userData } = await (await ApiService.GetUsers({ limit: 100, ...filters })).json();
          const { data: sitemateUserData } = await (
            await ApiService.GetUsers({ limit: 100, filters: { ...filters?.filters, userType: 'sitemate-users' } })
          ).json();

          const resultsMap = new Map();
          userData.forEach((user) => {
            if (user.firstName || user.lastName) {
              resultsMap.set(user.id, { label: EntityRefUtil.createUserFullNameRef(user), value: user.id });
            }
          });
          sitemateUserData.forEach((user) => {
            if (user.firstName || user.lastName) {
              resultsMap.set(user.id, { label: EntityRefUtil.createUserFullNameRef(user), value: user.id });
            }
          });

          if (value?.length) {
            const missingIds = Array.isArray(value) ? value.filter((id) => !resultsMap.has(id)) : [];
            if (missingIds.length) {
              const { data: missingUsers } = await (await ApiService.GetUsers({ limit: 100, filters: { id: missingIds } })).json();
              const { data: missingSitemateUsers } = await (
                await ApiService.GetUsers({ limit: 100, filters: { id: missingIds, userType: 'sitemate-users' } })
              ).json();

              for (const user of missingUsers) {
                resultsMap.set(user.id, { label: EntityRefUtil.createUserFullNameRef(user), value: user.id });
              }
              for (const user of missingSitemateUsers) {
                resultsMap.set(user.id, { label: EntityRefUtil.createUserFullNameRef(user), value: user.id });
              }
            }
          }

          return Array.from(resultsMap.values());
        },
      },
    }),
    // 15. Work Order
    columnHelper.accessor('workOrder', {
      id: 'workOrder',
      header: 'Work Order',
      cell: ({ getValue }) => {
        const value = getValue();
        if (!value) {
          return <span className="text-secondary-text">-</span>;
        }

        return (
          <Button
            variant="link"
            className="h-auto p-0 text-left"
            onClick={(e) => {
              e.stopPropagation();
              opts?.onWorkOrderClick?.(value.id);
            }}>
            {EntityRefUtil.createWorkOrderRef(value.number)}
          </Button>
        );
      },
      enableSorting: true,
      enableGrouping: false,
      enableColumnFilter: true,
      meta: {
        filterType: 'text',
      },
    }),
    createTagsColumn(columnHelper, { header: 'Schedule Check Tags', entity: TagEntity.Check, variant: 'violet' }),
    // 16. Dynamic per-unit columns: completed reading + last reading from summary
    ...(opts?.units
      ? opts.units.flatMap((unit) => [
          columnHelper.accessor((row) => row.completedReadings?.find((r) => r.unitName === unit.name)?.reading ?? null, {
            id: `completedReading-${unit.id}`,
            header: `Completed ${unit.abbreviation}`,
            cell: ({ getValue }) => {
              const value = getValue();
              if (value == null) return <span className="text-secondary-text">-</span>;
              return (
                <span>
                  {value} {unit.abbreviation}
                </span>
              );
            },
            enableSorting: false,
            enableGrouping: false,
            enableColumnFilter: false,
            meta: { columnGroup: 'Schedule Check Record' },
          }),
          columnHelper.accessor((row) => row.summary?.find((s) => s.unit === unit.name)?.currentAssetReading ?? null, {
            id: `lastReading-${unit.id}`,
            header: `Last Reading ${unit.abbreviation}`,
            cell: ({ getValue }) => {
              const value = getValue();
              return value != null ? <span>{value}</span> : <span className="text-secondary-text">-</span>;
            },
            enableSorting: false,
            enableGrouping: false,
            enableColumnFilter: false,
            meta: { columnGroup: ASSET_SECTION_LABEL },
          }),
        ])
      : []),
  ] as AccessorKeyColumnDef<GetChecksResponseDTO['data'][number]>[];
};

export const groupOptions = [
  { label: 'None', accessor: '' },
  ...createCheckTableColumns()
    .filter((column) => {
      const availableGroupColumns = ['asset', 'assetCategory', 'assetLocation', 'schedule', 'status', 'completedBy'];
      return !column.accessorKey && column.id ? availableGroupColumns.includes(column.id) : availableGroupColumns.includes(column.accessorKey);
    })
    .map((column) => ({ label: column.header?.toString() || '', accessor: !column.accessorKey && column.id ? column.id : column.accessorKey })),
] satisfies { label: string; accessor: string }[];

export const FILTER_FIELD_MAP: Record<
  NonNullable<ReturnType<typeof createCheckTableColumns>[number]['id'] | ReturnType<typeof createCheckTableColumns>[number]['accessorKey']>,
  keyof NonNullable<NonNullable<GetChecksRequestDTO['filters']>['fields']>
> = {
  asset: 'assetIds',
  assetCategory: 'assetCategoryIds',
  assetLocation: 'locationIds',
  assetTags: 'assetTagIds',
  schedule: 'scheduleIds',
  status: 'status',
  createdDate: 'createdDate',
  completedDate: 'completedDate',
  dueDate: 'dueDate',
  completedBy: 'completedById',
  workOrder: 'workOrderNumber',
  tags: 'tagIds',
};
